import { useEffect, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import { push } from 'connected-react-router';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { PageTitleBodyAndFooter } from 'src/app/components/Elements/PageTitleBodyAndFooter';
import { FLOW, MATCH_TYPE } from 'src/app/components/Pages/GetStarted/consts';
import { getTransferableMeds } from 'src/app/components/PageSections/DoctorTransferForm/utils';
import { loggedInTransferRx, medications } from 'src/app/router/paths';
import { resetNavBar, segmentTrack, toggleHideHeader } from 'src/app/state/actions';
import {
  getIsOnboardingDoctorTransfer,
  getOnboardingFlow,
  getOnboardingForm,
  getTransferEscriptCounts,
} from 'src/app/state/selectors';
import { openChat } from 'src/app/state/slices/chat';
import { useDispatch, useSelector } from 'src/app/state/store';
import { INTERSTITIAL_TIMING } from 'src/consts';
import { theme } from 'src/styles/theme';

import { ConfirmationVersion } from './components';
import { CONFIRMATION_TYPE, SUBHEADER, VERSION } from './consts';
import { SubHeader } from './styles';

interface GetVersionProps {
  flow: string;
  hasEscripts: boolean;
  hasMedications: boolean;
  matchType: string;
}

const getVersion = ({ flow, hasEscripts, hasMedications, matchType }: GetVersionProps) => {
  if (flow === FLOW.TRANSFER) {
    if (hasMedications) {
      return VERSION.TRANSFER;
    } else {
      return VERSION.NO_RX;
    }
  } else if (flow === FLOW.DOCTOR_ESCRIPT) {
    if (hasEscripts) {
      return VERSION.PROCESSING;
    } else if (hasMedications && matchType === MATCH_TYPE.FULL_ONLY_ONE_MATCH) {
      return VERSION.FOUND;
    } else {
      return VERSION.NOT_FOUND;
    }
  } else if (flow === FLOW.NO_RX) {
    return VERSION.NO_RX;
  }
  return VERSION.NOT_FOUND;
};

export const AccountCreationSuccess = () => {
  const escriptCounts = useSelector(getTransferEscriptCounts);
  const flow = useSelector(getOnboardingFlow);
  const isDoctorTransfer = useSelector(getIsOnboardingDoctorTransfer);
  const onboardingForm = useSelector(getOnboardingForm);
  const isAllMedsTransfer = !!onboardingForm.shouldTransferAllMeds;
  const onboardingMedications = isAllMedsTransfer
    ? [{ name: 'All medications', id: '', attributes: {} }]
    : onboardingForm.medications;

  const dispatch = useDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.mediumDown);

  const [shouldShowInterstitial, setShouldShowInterstitial] = useState(false);

  const hasEscripts = !!escriptCounts?.length;
  const hasMedications = !!onboardingMedications?.length;
  const matchType = JSON.parse(localStorage.getItem('personalizedOnboarding') || '{}')?.matchType;
  const version = getVersion({ flow, hasEscripts, hasMedications, matchType });

  const secondaryMarginBottom = version === VERSION.NO_RX ? (isMobile ? 's2' : 's3') : 'none';

  const showHeart = (callBack: () => any) => {
    dispatch(toggleHideHeader(true));
    setShouldShowInterstitial(true);
    window.setTimeout(() => {
      callBack();
      dispatch(resetNavBar());
    }, INTERSTITIAL_TIMING);
  };

  const goToMedicationsPage = () => dispatch(push(medications.toPath()));
  const goToTransferPage = () => {
    dispatch(push(loggedInTransferRx.toPath()));
  };

  const goToHomeWithChat = () => {
    goToMedicationsPage();
    dispatch(openChat());
  };

  const onChatClick = () => showHeart(goToHomeWithChat);
  const onContinue = () => {
    showHeart(goToMedicationsPage);
  };
  const onContinueToRxPage = () => showHeart(goToTransferPage);
  const onSkipForNowClick = () => {
    dispatch(
      segmentTrack(UNIFIED.skipForNow, {
        pagepath: window.location.pathname,
        source: 'Get Started Success No Rx',
      })
    );
    showHeart(goToMedicationsPage);
  };

  useEffect(() => {
    dispatch(segmentTrack(UNIFIED.viewOnboardingConfirmation, { confirmation_type: CONFIRMATION_TYPE[version] }));
  }, [dispatch, version]);

  const transferableMeds = onboardingMedications ? getTransferableMeds(onboardingMedications) : [];
  const hasTransferableMeds = transferableMeds.length > 0;

  const title =
    hasTransferableMeds || isAllMedsTransfer
      ? 'Welcome to Capsule'
      : 'Ask your doctor to send your prescriptions to Capsule';

  return (
    <PageTitleBodyAndFooter
      secondaryMarginBottom={secondaryMarginBottom}
      secondaryText={<SubHeader>{SUBHEADER[version]}</SubHeader>}
      title={title}
      tertiaryText={undefined}
      showInterstitial={shouldShowInterstitial}
    >
      <ConfirmationVersion
        escriptCounts={escriptCounts}
        isDoctorTransfer={isDoctorTransfer}
        medications={onboardingMedications}
        onChatClick={onChatClick}
        onContinue={onContinue}
        onContinueToRxPage={onContinueToRxPage}
        onSkipForNowClick={onSkipForNowClick}
        version={version}
      />
    </PageTitleBodyAndFooter>
  );
};
